import React from 'react';
import { Button, Card } from 'react-bootstrap';
import { GoPlus, GoX } from 'react-icons/go';
import ActionCard, { ActionCards } from '../../../../../controls/common/actionCard';
import StatusPill from '../../components/statusPill';
import { listMissions, listCells, hasCells } from '../../domain/model';
import { MISSION_STATUS } from '../../domain/schema';

/**
 * The rail's rows, from the store alone — there is no second source for a mission's title.
 *
 * A null count is "not swept yet", which is not the same fact as zero: counts are swept one mission
 * at a time (MissionsNode.sweepMissionCounts) and a count learned once is kept.
 * @param {Object} store
 * @returns {Array<{id: string, title: string, status: string, archived: boolean, cellCount: ?number}>}
 */
export function missionRailRows(store) {
  const rows = (store ? listMissions(store) : []).map((mission, index) => ({
    id: mission.id,
    title: mission.title || '',
    status: mission.status,
    archived: MISSION_STATUS.ARCHIVED === mission.status,
    cellCount: hasCells(store, mission.id) ? listCells(store, mission.id).length : null,
    index,
  }));
  // Archived last. The index rides along as the tie-break rather than relying on the sort being
  // stable, so the rail cannot re-order itself between two paints.
  rows.sort((a, b) => (Number(a.archived) - Number(b.archived)) || (a.index - b.index));
  return rows.map(({ index, ...row }) => row);
}

/**
 * MissionsRail — every mission in the session, one row each, and the door to a new one.
 *
 * It replaces the AG Grid switcher modal: the same four facts per mission, permanently on screen
 * rather than behind a button, which is what makes the board's own selection legible.
 *
 * A row is the revision menu's row with a mission's fields in its roles — the title, the id, the
 * cell count and the status — rather than a shape of its own; see
 * docs/specs/2026-10-14-mission-control-tweaks.md.
 *
 * Props:
 *   store      — the missions store
 *   selectedId — the mission on screen
 *   canEdit    — false withholds the Actions section entirely (the house rule for a blocked mutation:
 *                every card in one writes, so there is no read-only half of it — cellActions.js)
 *   creating   — a creation is in flight; the control is disabled rather than absent, because this
 *                one is blocked by something that is about to finish
 *   onSelect   — (missionId) => void
 *   onCreate   — () => void
 *   onHide     — () => void — the header's ×; absent draws no × at all
 *
 * The rail is a PANEL, headed like every other one in the dock: the title, the × that puts it away and
 * the rule under it. That × is the hide control — the board's strip keeps the way BACK and nothing else,
 * so a reader never meets two controls for one act (item 20).
 */
function MissionsRail({ store, selectedId, canEdit, creating, onSelect, onCreate, onHide }) {
  const rows = missionRailRows(store);
  return (
    <>
      {/* THE PANEL HEADER'S ×, rendered exactly as the Mission, Cell and Dependency panels render
          theirs: `.workflow-controls-pane-right` positions it against the Card, so it stays put while
          the list under it scrolls. */}
      {onHide && (
        <span className="workflow-controls-pane-right">
          <Button variant="light" onClick={onHide} aria-label="Hide the missions"><GoX /></Button>
        </span>
      )}

      <Card.Text as="h6">Missions</Card.Text>
      <hr />

      {/* NO "No missions" HERE. The board beside it already says so, and a second copy in the list is
          the list talking about itself. An empty rail is the answer (item 6). */}
      {/* The list is one object: the wrapper paints the box and the rows share their edges inside it,
          the way the component library's bordered table does. */}
      {rows.length > 0 && <div className="mc-rail-list">
        {rows.map(row => (
          <button
            type="button"
            key={row.id}
            className={`mc-rail-item mc-mission-row${row.id === selectedId ? ' on' : ''}` +
              `${row.archived ? ' is-archived' : ''}`}
            onClick={() => onSelect(row.id)}
          >
            {/* THE REVISION MENU'S ROW, role for role: the name first with the status mark pushed to the
                far end, the identifier in a <code> under it, then a muted meta line. `.record-title`,
                `.record-title-main` and `.record-meta` are src/index.css's own, shared with that menu
                and the blob list, so this row cannot drift from the idiom it is held against.
                The id sits in a <code> because `.mc-rail-item.on code` turns it teal on the selected row. */}
            <span className="record-title">
              <span className="record-title-main">
                <span className="mc-rail-item-name mc-mission-row-title">
                  {row.title || <span className="text-muted">Untitled</span>}
                </span>
              </span>
              <StatusPill status={row.status} />
            </span>
            <div className="record-meta">
              <span className="record-meta-value mc-mission-row-id"><code>{row.id}</code></span>
              <span className="mc-mission-row-count text-muted">
                {null === row.cellCount ? '—' : `${row.cellCount} cell${1 === row.cellCount ? '' : 's'}`}
              </span>
            </div>
          </button>
        ))}
      </div>}

      {/* + NEW MISSION IS AN ACTION, in the section this app puts actions in — `Card.Text as="h6"` with
          no `hr` (ActionCards' own top margin is that rule) and one `ActionCard` carrying the control
          and what it does. Below the list, because the list is what the rail IS and an act on it comes
          after it (item 21).
          GREEN, like every other act in one of these sections: the playground's own
          `<Button variant="success" size="sm"><GoPlus /> New Object</Button>` is this button with a
          different noun (details/playground.js), and the glyph's size and spacing come from the frame
          (`.action-cards .btn`), not from here. */}
      {false !== canEdit && (
        <>
          <Card.Text as="h6" className="mt-3">Actions</Card.Text>
          <ActionCards>
            <ActionCard helper="Creates an empty mission and shows it on the board">
              <Button variant="success" size="sm" disabled={!!creating} onClick={onCreate}>
                <GoPlus /> New Mission
              </Button>
            </ActionCard>
          </ActionCards>
        </>
      )}
    </>
  );
}

export default MissionsRail;
